import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Button } from "@gss/components/Button";
import { Col } from "@gss/components/Col";
import { useMessages } from "@gss/components/Messages";
import { Row } from "@gss/components/Row";
import { Select } from "@gss/components/Select";
import { isSecurityContext } from "@gss/types/SecurityContext";
import { DAO } from "../DAO";
import { isAllOption, OPTION_ALL } from "../filters/FilterUtil";
import type {
    AggregateOn,
    AugmentedExportResultForm,
    Option,
} from "./export.types";

interface Props {
    conf: AugmentedExportResultForm;
}

export function ExportAgg({ conf }: Props): JSX.Element | null {
    const [aggregateOn, setAggregateOn] = useState<AggregateOn>();
    const [securityContext, setSecurityContext] = useState<string>();
    const [sex, setSex] = useState<string>();
    const { addError } = useMessages();

    useEffect(() => {
        setAggregateOn(conf.aggregateOptions[0].value);
        setSecurityContext(conf.securityContexts[0].value);
        setSex(conf.sex[0]?.value ?? OPTION_ALL);
    }, []);

    if (aggregateOn == null || securityContext == null || sex == null) {
        return null;
    }

    const downloadSpreadsheet = () => {
        if (!isSecurityContext(securityContext)) {
            throw new Error(`Unknown security context: ${securityContext}`);
        }
        const filters = isAllOption(sex) ? [] : [`sex:${sex}`];
        DAO.export
            .downloadAggregate(
                securityContext,
                conf.measurementName,
                conf.periods,
                aggregateOn,
                filters,
            )
            .catch(addError);
    };

    return (
        <div>
            {renderDropdown(
                "Aggregera på",
                aggregateOn,
                conf.aggregateOptions,
                setAggregateOn,
            )}
            {renderDropdown(
                "Publik/inloggad",
                securityContext,
                conf.securityContexts,
                setSecurityContext,
            )}
            {renderDropdown("Kön", sex, conf.sex, setSex)}

            <div>
                <Button variant="primary" onClick={downloadSpreadsheet}>
                    Hämta kalkylblad
                </Button>
            </div>
        </div>
    );
}

function renderDropdown<T extends string>(
    title: string,
    value: T,
    options: Option<T>[],
    setValue: (value: T) => void,
): JSX.Element | null {
    if (options.length === 0) {
        return null;
    }
    return (
        <Row>
            <Col xs="2" className="mb-1">
                {title}
            </Col>
            <Col xs="4" className="mb-1">
                <Select value={value} onChange={setValue}>
                    {options.map((o) => ({ value: o.value, children: o.name }))}
                </Select>
            </Col>
        </Row>
    );
}
